import { useEffect, useRef } from "react";

import type { OpsAlert } from "../api/types";
import { useNow } from "../hooks/useNow";
import { useOpsAlertsFeed } from "../hooks/useOpsAlerts";
import { chime } from "../lib/chime";
import {
  emptyReason,
  isExpired,
  isLive,
  sortAlerts,
  stateLabel,
  title,
  unannounced,
} from "../lib/opscard";
import { useOpsAlerts } from "../store/useOpsAlerts";
import { useSignalSound } from "../store/useSignalSound";

/**
 * Операционные тревоги (веха M8) — полоса под аварийным стопом.
 *
 * # Чем это НЕ является
 *
 * Это не торговая карточка. LockAlerts и SignalAlerts предлагают СДЕЛКУ, а
 * здесь сообщается о поломке стека: разорванный WS, молчащий трейдер,
 * застрявшая очередь. Два смысла под одним словом «алерт» и разводит имя
 * подсистемы — opswatch.
 *
 * # Почему выше торговых карточек, но ниже стопа
 *
 * Операционное состояние ближе к аварийному стопу, чем к торговым предложениям:
 * пока терминал не видит рынка, предложения по нему не стоят ничего. Но
 * вытеснять стоп оно не вправе — тот единственный, чьё положение решает, уйдут
 * ли деньги.
 *
 * # Вся логика — в lib/opscard.ts
 *
 * Компонентных тестов в проекте нет вовсе (vitest собирает только .test.ts),
 * поэтому здесь остаётся разметка, а всё, что можно сломать молча — порядок,
 * звонок, гашение по сроку, — живёт в .ts и покрыто тестами.
 */
export function OpsAlerts() {
  const { failed, known } = useOpsAlertsFeed();
  const alerts = useOpsAlerts((s) => s.alerts);
  const drop = useOpsAlerts((s) => s.drop);
  const soundOn = useSignalSound((s) => s.on);

  // Секундный тик нужен только гашению снятых карточек по сроку.
  const now = useNow(1000);

  const announced = useRef<Set<string>>(new Set());
  const list = sortAlerts(Object.values(alerts));

  useEffect(() => {
    const { ring, next } = unannounced(list, announced.current);
    // Пометка ставится ВСЕГДА, даже при выключенном звуке: иначе включение
    // звука дало бы залп по всему, что накопилось за смену.
    announced.current = next;
    if (!soundOn) return;
    for (const _ of ring) chime();
  }, [list, soundOn]);

  // Снятые карточки гаснут сами; живые висят, пока держится тревога.
  useEffect(() => {
    for (const a of list) {
      if (isExpired(a, TTL_SEC, now)) drop(a.id);
    }
  }, [list, now, drop]);

  const reason = emptyReason(known, failed);
  if (list.length === 0 && !reason) return null;

  return (
    <div className="flex flex-col gap-1">
      {reason && (
        <div className="rounded border border-neutral-700 bg-neutral-900/60 px-2 py-1 text-xs text-neutral-400">
          {reason}
        </div>
      )}
      {list.map((a) => (
        <Row key={a.id} alert={a} />
      ))}
    </div>
  );
}

/**
 * TTL карточки на экране.
 *
 * Совпадает с умолчанием OPS_CARD_TTL. Сервер присылает своё значение в
 * конверте сокета, но карточка может приехать и по REST — без конверта, —
 * поэтому число дублируется здесь. Расхождение безобидно: оно решает лишь то,
 * сколько СНЯТАЯ тревога ещё повисит.
 */
const TTL_SEC = 600;

function Row({ alert }: { alert: OpsAlert }) {
  const live = isLive(alert);
  return (
    <div
      className={`flex items-baseline gap-2 rounded border px-2 py-1 text-xs ${border(alert)} ${
        live ? "" : "opacity-60"
      }`}
    >
      <span className={`font-semibold ${color(alert)}`}>{severityGlyph(alert)}</span>
      <span className="font-mono text-neutral-300">{title(alert)}</span>
      <span className="flex-1 truncate text-neutral-200">{alert.text}</span>
      {alert.value && <span className="font-mono text-neutral-500">{alert.value}</span>}
      <span className="whitespace-nowrap text-neutral-500">{stateLabel(alert)}</span>
    </div>
  );
}

/**
 * Значок уровня.
 *
 * Словом, а не цветом одним: цвет теряется при дальтонизме и на плохом мониторе,
 * а полоса тревог — последнее место, где стоит на него полагаться.
 */
function severityGlyph(a: OpsAlert): string {
  if (a.severity === "crit") return "СТОП";
  if (a.severity === "warn") return "ВНИМ";
  return "инфо";
}

function color(a: OpsAlert): string {
  if (a.severity === "crit") return "text-red-400";
  if (a.severity === "warn") return "text-amber-400";
  return "text-neutral-400";
}

function border(a: OpsAlert): string {
  if (!isLive(a)) return "border-neutral-800 bg-neutral-900/40";
  if (a.severity === "crit") return "border-red-800 bg-red-950/40";
  if (a.severity === "warn") return "border-amber-800 bg-amber-950/30";
  return "border-neutral-700 bg-neutral-900/60";
}
